import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Wallet,
  TrendingUp,
  AlertTriangle,
  Download,
  ChevronDown,
  ChevronRight,
  IndianRupee,
  CheckCircle2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatINR = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const formatINR2 = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const LiquidityWidget = ({ tournament }) => {
  const [asOf, setAsOf] = useState(new Date().toISOString().split('T')[0]);
  const [granularity, setGranularity] = useState('daily');
  const [trendDays, setTrendDays] = useState(30);
  const [snapshot, setSnapshot] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bankExpanded, setBankExpanded] = useState(false);
  const [fdExpanded, setFdExpanded] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const asOfISO = new Date(asOf).toISOString();
      const endDate = new Date(asOf);
      const startDate = new Date(asOf);
      startDate.setDate(startDate.getDate() - trendDays);

      const [snapRes, trendRes] = await Promise.all([
        axios.get(`${API}/analytics/liquidity`, {
          params: { as_of: asOfISO, tournament },
          withCredentials: true,
        }),
        axios.get(`${API}/analytics/liquidity-trend`, {
          params: {
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            granularity,
            tournament,
          },
          withCredentials: true,
        }),
      ]);
      setSnapshot(snapRes.data);
      setTrend(trendRes.data?.points || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load liquidity data');
    } finally {
      setLoading(false);
    }
  }, [asOf, granularity, trendDays, tournament]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportInvoicesDue = async () => {
    try {
      const asOfISO = new Date(asOf).toISOString();
      const res = await axios.get(`${API}/analytics/invoices-due`, {
        params: { as_of: asOfISO, tournament, format: 'csv' },
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices_due_${asOf}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Invoices-due list downloaded');
    } catch {
      toast.error('Export failed');
    }
  };

  const segments = useMemo(() => {
    if (!snapshot) return [];
    const total = (snapshot.total_liquidity || 0) + (snapshot.committed_but_unbilled || 0);
    if (total <= 0) return [];
    return [
      { label: 'Invoices due', amount: snapshot.invoices_due, color: 'bg-red-500' },
      { label: 'Unbilled PO', amount: snapshot.committed_but_unbilled, color: 'bg-amber-500' },
      { label: 'Free liquidity', amount: Math.max(0, snapshot.free_liquidity - snapshot.committed_but_unbilled), color: 'bg-primary' },
    ];
  }, [snapshot]);
  const segmentTotal = segments.reduce((s, x) => s + (x.amount || 0), 0) || 1;

  if (!snapshot && loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading liquidity...
        </CardContent>
      </Card>
    );
  }

  const coverageRatio = snapshot?.liquidity_coverage_ratio || 0;
  const coverageColor =
    coverageRatio >= 100 ? 'text-primary' : coverageRatio >= 75 ? 'text-amber-600' : 'text-destructive';
  const bufferMsg = snapshot?.liquidity_after_all_pos >= 0
    ? `Liquidity covers due invoices + un-invoiced PO commitments with ${formatINR(snapshot.liquidity_after_all_pos)} to spare.`
    : `Liquidity short by ${formatINR(Math.abs(snapshot?.liquidity_after_all_pos || 0))} once due invoices + un-invoiced PO commitments are honoured.`;

  return (
    <div className="space-y-4" data-testid="liquidity-widget">
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Wallet className="h-5 w-5 text-primary" />
                Liquidity Position
              </CardTitle>
              <CardDescription>
                {tournament || 'All tournaments'} · as of {new Date(asOf).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs text-muted-foreground whitespace-nowrap">As of</Label>
              <Input
                type="date"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
                className="w-40"
                data-testid="liquidity-as-of"
              />
              <Label className="text-xs text-muted-foreground whitespace-nowrap">Trend</Label>
              <Select value={granularity} onValueChange={setGranularity}>
                <SelectTrigger className="w-28" data-testid="liquidity-granularity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(trendDays)} onValueChange={(v) => setTrendDays(parseInt(v))}>
                <SelectTrigger className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="180">180 days</SelectItem>
                  <SelectItem value="365">365 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* A. Bank Balances - Collapsible */}
          <Collapsible open={bankExpanded} onOpenChange={setBankExpanded}>
            <div className="flex items-center justify-between border rounded-md p-3 bg-muted/20">
              <div className="flex items-center gap-3 min-w-0">
                <CollapsibleTrigger className="flex items-center gap-2 text-left" data-testid="toggle-bank-details">
                  {bankExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">A. Bank Balances</div>
                    <div className="text-xs text-muted-foreground">{snapshot?.bank_details?.length || 0} account(s)</div>
                  </div>
                </CollapsibleTrigger>
              </div>
              <div className="font-mono text-lg font-bold text-primary">{formatINR(snapshot?.bank_total)}</div>
            </div>
            <CollapsibleContent className="mt-2 space-y-1 px-3">
              {(snapshot?.bank_details || []).length === 0 ? (
                <div className="text-xs text-muted-foreground py-2">No bank accounts yet. Add them in the Bank tab.</div>
              ) : (
                (snapshot?.bank_details || []).map((b) => (
                  <div key={b.account_id} className="flex items-center justify-between py-1 text-sm">
                    <div>
                      <span className="font-medium">{b.account_name}</span>
                      {b.balance_date ? (
                        <span className="text-xs text-muted-foreground ml-2">
                          as of {new Date(b.balance_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      ) : (
                        <span className="text-xs text-destructive ml-2">no balance recorded</span>
                      )}
                    </div>
                    <span className="font-mono">{formatINR(b.amount)}</span>
                  </div>
                ))
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* B. Fixed Deposit — shown as Corpus (long-term reserve), NOT part of
              liquidity decision-making per CFO mandate. Bank balance is the
              sole basis for all liquidity metrics below. */}
          <Collapsible open={fdExpanded} onOpenChange={setFdExpanded}>
            <div className="flex items-center justify-between border rounded-md p-3 bg-amber-50/40 border-amber-200">
              <CollapsibleTrigger className="flex items-center gap-2 text-left">
                {fdExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                    B. Corpus (Fixed Deposits)
                    <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-400 text-amber-700 bg-amber-50">
                      Not available for normal usage
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {snapshot?.fd_details?.length || 0} account(s) · excluded from liquidity
                  </div>
                </div>
              </CollapsibleTrigger>
              <div className="font-mono text-lg font-bold text-amber-700">{formatINR(snapshot?.fd_total)}</div>
            </div>
            <CollapsibleContent className="mt-2 space-y-1 px-3">
              {(snapshot?.fd_details || []).length === 0 ? (
                <div className="text-xs text-muted-foreground py-2">No FD accounts yet.</div>
              ) : (
                (snapshot?.fd_details || []).map((b) => (
                  <div key={b.account_id} className="flex items-center justify-between py-1 text-sm">
                    <div>
                      <span className="font-medium">{b.account_name}</span>
                      {b.balance_date ? (
                        <span className="text-xs text-muted-foreground ml-2">
                          as of {new Date(b.balance_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      ) : (
                        <span className="text-xs text-destructive ml-2">no balance recorded</span>
                      )}
                    </div>
                    <span className="font-mono">{formatINR(b.amount)}</span>
                  </div>
                ))
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* PO Committed + Invoices Due */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">PO Committed (total issued)</div>
                  <div className="text-xs text-muted-foreground">Gross: Taxable + GST</div>
                </div>
                <div className="font-mono text-lg font-bold">{formatINR(snapshot?.po_committed)}</div>
              </div>
            </div>
            <div className="border rounded-md p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">Invoices Due for Payment</div>
                  <div className="text-xs text-muted-foreground">{snapshot?.invoices_due_count || 0} unpaid invoice(s)</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-lg font-bold text-destructive">{formatINR(snapshot?.invoices_due)}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportInvoicesDue}
                    disabled={!snapshot?.invoices_due_count}
                    data-testid="export-invoices-due"
                  >
                    <Download className="h-4 w-4 mr-1" /> CSV
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Derived metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <MetricCard
              label="Total Liquidity (Bank only)"
              value={formatINR(snapshot?.total_liquidity)}
              sub={`Bank: ${formatINR(snapshot?.bank_total)} · FD corpus excluded`}
            />
            <MetricCard
              label="Free Liquidity (after invoices)"
              value={formatINR(snapshot?.free_liquidity)}
              sub="Bank balance after paying all due invoices"
              valueClass="text-primary"
            />
            <MetricCard
              label="Liquidity after open POs"
              value={formatINR(snapshot?.liquidity_after_all_pos)}
              sub="After paying due invoices + un-invoiced PO commitments"
              valueClass={snapshot?.liquidity_after_all_pos >= 0 ? 'text-primary' : 'text-destructive'}
            />
            <MetricCard
              label="Corpus (FD reserve)"
              value={formatINR(snapshot?.fd_total)}
              sub="Long-term — not available for daily ops"
              valueClass="text-amber-700"
            />
            <MetricCard
              label="Committed but Unbilled"
              value={formatINR(snapshot?.committed_but_unbilled)}
              sub="PO issued, invoice not yet received"
            />
            <MetricCard
              label="PO Execution Rate"
              value={`${(snapshot?.po_execution_rate || 0).toFixed(1)}%`}
              sub="Invoices billed vs total PO value"
              progress={Math.min(100, snapshot?.po_execution_rate || 0)}
            />
            <MetricCard
              label="Liquidity Coverage Ratio"
              value={`${(coverageRatio).toFixed(1)}%`}
              sub="Bank liquidity ÷ un-invoiced PO commitments"
              valueClass={coverageColor}
              progress={Math.min(100, coverageRatio)}
            />
          </div>

          {/* Advisory banner */}
          {(snapshot?.po_committed || 0) > 0 && (
            <div
              className={`rounded-md p-3 flex items-center gap-2 text-sm ${
                snapshot.liquidity_after_all_pos >= 0
                  ? 'bg-primary/10 text-primary border border-primary/30'
                  : 'bg-destructive/10 text-destructive border border-destructive/30'
              }`}
            >
              {snapshot.liquidity_after_all_pos >= 0 ? (
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              ) : (
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              )}
              {bufferMsg}
            </div>
          )}

          {/* Visual breakdown */}
          {segmentTotal > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Visual Breakdown</div>
              <div className="text-xs text-muted-foreground">Liquidity composition</div>
              <div className="flex h-4 w-full rounded overflow-hidden border">
                {segments.map((s) => (
                  <div
                    key={`bar-${s.label}`}
                    className={s.color}
                    style={{ width: `${(s.amount / segmentTotal) * 100}%` }}
                    title={`${s.label}: ${formatINR(s.amount)}`}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-4 text-xs">
                {segments.map((s) => (
                  <div key={`legend-${s.label}`} className="flex items-center gap-1.5">
                    <span className={`inline-block w-3 h-3 rounded ${s.color}`} />
                    <span>
                      {s.label}: <span className="font-mono">{formatINR(s.amount)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trend chart */}
      {trend.length > 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-heading">Liquidity Trend ({granularity} · last {trendDays} days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(v) => formatINR2(v)}
                    labelFormatter={(l) => new Date(l).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                  <Line type="monotone" dataKey="total_liquidity" name="Total Liquidity" stroke="#0f766e" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="po_committed" name="PO Committed" stroke="#d97706" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="invoices_due" name="Invoices Due" stroke="#dc2626" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="free_liquidity" name="Free Liquidity" stroke="#16a34a" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, sub, valueClass = '', progress = null }) => (
  <div className="border rounded-md p-3">
    <div className={`text-2xl font-bold font-mono ${valueClass}`}>{value}</div>
    <div className="text-sm font-medium mt-1">{label}</div>
    {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    {progress !== null && (
      <div className="mt-2 h-1.5 w-full bg-muted rounded overflow-hidden">
        <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
      </div>
    )}
  </div>
);

export default LiquidityWidget;
